import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { arabicErrorFor } from '../utils/serviceErrorMessages';
import SubscriptionPricingPanel from '../components/services/SubscriptionPricingPanel';
import { CUSTOM_PRICING_UI_ENABLED } from '../components/services/cleaningPricingConfig';
import '../styles/CreateServiceClient.css';

// Stage 6c — atomic onboarding wizard.
// Per-step writes are deferred; final submit creates User+Client+Location
// (+ optional Subscription+Invoice) in a single backend transaction.

const STEP_DEFS = {
  client: { title: 'بيانات العميل', sub: 'الاسم والبريد ونوع العميل' },
  location: { title: 'بيانات الموقع', sub: 'عنوان ومواصفات موقع التنظيف' },
  subscription: { title: 'الاشتراك', sub: 'الباقة والفترة والسعر' }
};

// Bedrooms → tier mapping mirrors backend pricingService.resolvePricingTier
const tierFromBedrooms = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseInt(raw, 10);
  if (Number.isNaN(n) || n < 0) return null;
  if (n === 0) return 'studio';
  if (n >= 5) return '5br';
  return `${n}br`;
};

const CreateServiceClient = () => {
  const navigate = useNavigate();

  const [activeNavItem, setActiveNavItem] = useState('services');
  const [step, setStep] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Result IDs from the onboard response (only populated after submit)
  const [created, setCreated] = useState({
    clientId: null,
    locationId: null,
    subscriptionId: null,
    dashboardUrl: null
  });

  const [clientForm, setClientForm] = useState({
    name: '', email: '', phone: '', type: 'residential', companyName: '', notes: ''
  });

  const [locationForm, setLocationForm] = useState({
    name: '', type: 'residential', city: '', district: '',
    bedrooms: '', bathrooms: '', sqm: '',
    walkIn: false
  });

  const [subForm, setSubForm] = useState({
    packageCode: '',
    discountAmount: '',
    billingCycleStart: '',
    priceNotes: '',
    listPrice: '',
    visitsIncluded: '',
    validityDays: '',
    billingCycleEnd: ''
  });

  const STEPS = useMemo(
    () => locationForm.walkIn
      ? [STEP_DEFS.client, STEP_DEFS.location]
      : [STEP_DEFS.client, STEP_DEFS.location, STEP_DEFS.subscription],
    [locationForm.walkIn]
  );

  const locationTier = useMemo(
    () => tierFromBedrooms(locationForm.bedrooms),
    [locationForm.bedrooms]
  );

  // Tier change invalidates any previously-picked package + custom-path fields.
  // Defends against the bedrooms-edited-after-Step-3 case (would otherwise
  // submit a stale packageCode and trip PRICING_TIER_MISMATCH server-side).
  useEffect(() => {
    setSubForm(prev => ({
      ...prev,
      packageCode: '',
      listPrice: '',
      visitsIncluded: '',
      validityDays: '',
      billingCycleEnd: ''
    }));
  }, [locationTier]);

  const handleClientChange = (field, value) => setClientForm(prev => ({ ...prev, [field]: value }));
  const handleLocationChange = (field, value) => setLocationForm(prev => ({ ...prev, [field]: value }));
  const handleSubChange = (field, value) => setSubForm(prev => ({ ...prev, [field]: value }));

  // ----- Validators (no DB writes — pure form checks) -----
  const validateClient = () => {
    if (!clientForm.name || !clientForm.email || !clientForm.phone || !clientForm.type) {
      return 'الاسم والبريد الإلكتروني ورقم الجوال ونوع العميل مطلوبة';
    }
    return null;
  };

  const validateLocation = () => {
    if (!locationForm.name || !locationForm.type) return 'اسم الموقع ونوعه مطلوبان';
    if (locationForm.bedrooms === '' || locationForm.bedrooms === null || locationForm.bedrooms === undefined) {
      return 'عدد غرف النوم مطلوب — أدخل 0 لاستوديو';
    }
    const n = parseInt(locationForm.bedrooms, 10);
    if (Number.isNaN(n) || n < 0) return 'عدد غرف النوم يجب أن يكون رقماً صحيحاً 0 أو أكبر';
    return null;
  };

  const validateSubscription = () => {
    if (!subForm.billingCycleStart) return 'تاريخ بداية الدورة مطلوب';
    // Catalog-only mode: require packageCode (custom path hidden — see
    // cleaningPricingConfig.js). When flag is on, fall back to legacy gate.
    if (!CUSTOM_PRICING_UI_ENABLED && !subForm.packageCode) {
      return 'يرجى اختيار باقة';
    }
    if (CUSTOM_PRICING_UI_ENABLED && !subForm.packageCode) {
      if (!subForm.listPrice || !subForm.visitsIncluded) {
        return 'السعر القياسي وعدد الزيارات مطلوبان للسعر المخصص';
      }
    }
    return null;
  };

  // ----- Submit (single atomic call) -----
  const buildPayload = () => {
    const payload = {
      client: {
        name: clientForm.name,
        email: clientForm.email,
        phone: clientForm.phone,
        type: clientForm.type,
        companyName: clientForm.companyName || undefined,
        notes: clientForm.notes || undefined
      },
      location: {
        name: locationForm.name,
        type: locationForm.type,
        address: {
          city: locationForm.city || undefined,
          district: locationForm.district || undefined
        },
        size: {
          bedrooms: parseInt(locationForm.bedrooms, 10),
          bathrooms: locationForm.bathrooms ? parseInt(locationForm.bathrooms, 10) : undefined,
          sqm: locationForm.sqm ? parseInt(locationForm.sqm, 10) : undefined
        }
      },
      walkIn: !!locationForm.walkIn
    };

    if (!locationForm.walkIn) {
      const sub = {
        billingCycleStart: subForm.billingCycleStart,
        priceNotes: subForm.priceNotes || undefined,
        discountAmount: subForm.discountAmount ? parseFloat(subForm.discountAmount) : undefined
      };
      if (subForm.packageCode) {
        sub.packageCode = subForm.packageCode;
      } else {
        sub.listPrice = parseFloat(subForm.listPrice);
        sub.visitsIncluded = parseInt(subForm.visitsIncluded, 10);
        if (subForm.validityDays) sub.validityDays = parseInt(subForm.validityDays, 10);
        if (subForm.billingCycleEnd) sub.billingCycleEnd = subForm.billingCycleEnd;
      }
      payload.subscription = sub;
    }

    return payload;
  };

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);
    try {
      const res = await serviceApi.onboardClient(buildPayload());
      const data = res.data || {};
      setCreated({
        clientId: data.client?._id || null,
        locationId: data.location?._id || null,
        subscriptionId: data.subscription?._id || null,
        dashboardUrl: data.dashboardUrl || null
      });
      setIsSuccess(true);
    } catch (err) {
      setError(arabicErrorFor(err));
    } finally {
      setSaving(false);
    }
  };

  // ----- Step navigation -----
  const handleNext = async () => {
    setError(null);
    if (step === 0) {
      const e = validateClient();
      if (e) { setError(e); return; }
      setStep(1);
      return;
    }
    if (step === 1) {
      const e = validateLocation();
      if (e) { setError(e); return; }
      if (locationForm.walkIn) {
        await handleSubmit();
      } else {
        setStep(2);
      }
      return;
    }
    if (step === 2) {
      const e = validateSubscription();
      if (e) { setError(e); return; }
      await handleSubmit();
    }
  };

  const stepButtonLabel = () => {
    if (step === 0) return 'التالي';
    if (step === 1) return locationForm.walkIn ? 'إنشاء العميل' : 'التالي';
    return 'إنشاء الاشتراك';
  };

  // ============= RENDER =============

  if (isSuccess) {
    const fullUrl = created.dashboardUrl
      ? `${window.location.origin}/#${created.dashboardUrl}`
      : '';
    return (
      <DesktopLayout
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="إضافة عميل جديد"
      >
        <div className="svc-wizard">
          <div className="svc-wizard__success">
            <div className="svc-wizard__success-icon"><i className="bi bi-check-circle-fill" /></div>
            <div className="svc-wizard__success-title">تم إنشاء العميل بنجاح</div>
            <div className="svc-wizard__success-sub">
              تم إرسال بريد إلكتروني للعميل يتضمن رابط لوحة التحكم الخاصة به
            </div>
            {fullUrl && (
              <>
                <div className="svc-wizard__success-sub" style={{ marginTop: '4px' }}>
                  يمكنك أيضاً إرسال الرابط التالي عبر الواتساب
                </div>
                <div className="svc-wizard__link-box">{fullUrl}</div>
              </>
            )}
            <div className="svc-wizard__actions" style={{ justifyContent: 'center' }}>
              {fullUrl && (
                <button
                  className={`svc-action ${copied ? 'svc-action--outline' : 'svc-action--primary'}`}
                  onClick={() => {
                    navigator.clipboard.writeText(fullUrl).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    });
                  }}
                >
                  <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`} />
                  {copied ? 'تم النسخ' : 'نسخ الرابط'}
                </button>
              )}
              <button
                className="svc-action svc-action--outline"
                onClick={() => navigate(`/services/clients/${created.clientId}`)}
              >
                <i className="bi bi-eye" />
                عرض العميل
              </button>
            </div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  const renderStep = () => {
    if (step === 0) {
      return (
        <div className="svc-wizard__form">
          <div className="svc-wizard__field">
            <label className="svc-wizard__label">اسم العميل *</label>
            <input
              className="svc-wizard__input touch-input"
              value={clientForm.name}
              onChange={(e) => handleClientChange('name', e.target.value)}
              placeholder="الاسم الكامل"
            />
          </div>
          <div className="svc-wizard__row">
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">البريد الإلكتروني *</label>
              <input
                className="svc-wizard__input touch-input"
                type="email"
                value={clientForm.email}
                onChange={(e) => handleClientChange('email', e.target.value)}
                placeholder="email@example.com"
                dir="ltr"
              />
            </div>
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">رقم الجوال *</label>
              <input
                className="svc-wizard__input touch-input"
                type="tel"
                value={clientForm.phone}
                onChange={(e) => handleClientChange('phone', e.target.value)}
                placeholder="05xxxxxxxx"
                dir="ltr"
                required
              />
            </div>
          </div>
          <div className="svc-wizard__row">
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">نوع العميل *</label>
              <select
                className="svc-wizard__input svc-wizard__input--select touch-input"
                value={clientForm.type}
                onChange={(e) => handleClientChange('type', e.target.value)}
              >
                <option value="residential">سكني</option>
                <option value="str-owner">مالك وحدات إيجار قصير</option>
                <option value="commercial">تجاري</option>
              </select>
            </div>
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">اسم الشركة</label>
              <input
                className="svc-wizard__input touch-input"
                value={clientForm.companyName}
                onChange={(e) => handleClientChange('companyName', e.target.value)}
                placeholder="اختياري"
              />
            </div>
          </div>
          <div className="svc-wizard__field">
            <label className="svc-wizard__label">ملاحظات</label>
            <textarea
              className="svc-wizard__input touch-input"
              value={clientForm.notes}
              onChange={(e) => handleClientChange('notes', e.target.value)}
              rows={2}
              placeholder="ملاحظات إضافية..."
            />
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="svc-wizard__form">
          <div className="svc-wizard__row">
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">اسم الموقع *</label>
              <input
                className="svc-wizard__input touch-input"
                value={locationForm.name}
                onChange={(e) => handleLocationChange('name', e.target.value)}
                placeholder="مثال: شقة الحمراء"
              />
            </div>
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">نوع الموقع *</label>
              <select
                className="svc-wizard__input svc-wizard__input--select touch-input"
                value={locationForm.type}
                onChange={(e) => handleLocationChange('type', e.target.value)}
              >
                <option value="residential">سكني</option>
                <option value="str-unit">وحدة إيجار قصير</option>
                <option value="office">مكتب</option>
                <option value="commercial">تجاري</option>
              </select>
            </div>
          </div>
          <div className="svc-wizard__row">
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">المدينة</label>
              <input
                className="svc-wizard__input touch-input"
                value={locationForm.city}
                onChange={(e) => handleLocationChange('city', e.target.value)}
                placeholder="جدة"
              />
            </div>
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">الحي</label>
              <input
                className="svc-wizard__input touch-input"
                value={locationForm.district}
                onChange={(e) => handleLocationChange('district', e.target.value)}
                placeholder="الحمراء"
              />
            </div>
          </div>
          <div className="svc-wizard__field">
            <label className="svc-wizard__label">غرف النوم *</label>
            <input
              className="svc-wizard__input touch-input"
              type="number"
              min="0"
              value={locationForm.bedrooms}
              onChange={(e) => handleLocationChange('bedrooms', e.target.value)}
              placeholder="مثال: 2"
            />
            <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: 4 }}>
              أدخل 0 لاستوديو
            </div>
          </div>
          <div className="svc-wizard__row">
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">الحمامات</label>
              <input
                className="svc-wizard__input touch-input"
                type="number"
                min="0"
                value={locationForm.bathrooms}
                onChange={(e) => handleLocationChange('bathrooms', e.target.value)}
                placeholder="2"
              />
            </div>
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">المساحة (م²)</label>
              <input
                className="svc-wizard__input touch-input"
                type="number"
                min="0"
                value={locationForm.sqm}
                onChange={(e) => handleLocationChange('sqm', e.target.value)}
                placeholder="120"
              />
            </div>
          </div>

          {/* Walk-in toggle (decision #49 — conditional Step 3) */}
          <div className="svc-wizard__field" style={{ marginTop: 8, padding: 12, background: '#f8f9fa', borderRadius: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={locationForm.walkIn}
                onChange={(e) => handleLocationChange('walkIn', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: '#471396' }}
              />
              <span style={{ fontSize: '0.92rem', fontWeight: 600 }}>
                عميل بدون اشتراك (زيارة واحدة)
              </span>
            </label>
            <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: 4, marginRight: 26 }}>
              تخطي إعداد الاشتراك — يمكن إضافة طلب زيارة فردية لاحقاً
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      // Use the shared pricing panel — same UX as CreateSubscriptionModal.
      // Tier is derived from bedrooms (location not yet persisted).
      return (
        <div className="svc-wizard__form" dir="rtl">
          <SubscriptionPricingPanel
            locationTier={locationTier}
            locationId={null}
            values={subForm}
            onChange={handleSubChange}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="إضافة عميل جديد"
    >
      <div className="svc-wizard">
        <button className="svc-wizard__back" onClick={() => navigate('/services/clients')}>
          <i className="bi bi-arrow-right" />
          العودة للقائمة
        </button>

        {/* Progress dots — count adapts to walk-in */}
        <div className="svc-wizard__progress">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`svc-wizard__step-dot ${
                i < step ? 'svc-wizard__step-dot--done' :
                i === step ? 'svc-wizard__step-dot--active' : ''
              }`}
            />
          ))}
        </div>

        <div className="svc-wizard__step-title">{STEPS[step]?.title}</div>
        <div className="svc-wizard__step-sub">{STEPS[step]?.sub}</div>

        {error && (
          <div className="svc-wizard__error">
            <i className="bi bi-exclamation-circle" />
            {error}
          </div>
        )}

        {renderStep()}

        <div className="svc-wizard__actions" style={{ marginTop: 24 }}>
          <button
            className="svc-action svc-action--primary"
            onClick={handleNext}
            disabled={saving}
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-arrow-left" />
            )}
            {stepButtonLabel()}
          </button>
          {step > 0 && (
            <button
              className="svc-action svc-action--outline"
              onClick={() => { setStep(step - 1); setError(null); }}
              disabled={saving}
            >
              <i className="bi bi-arrow-right" />
              السابق
            </button>
          )}
        </div>
      </div>
    </DesktopLayout>
  );
};

export default CreateServiceClient;
